/**
 * Composable pour la gestion centralisée des métadonnées SEO
 */
export const useSeo = () => {
  const config = useRuntimeConfig()
  const route = useRoute()

  // Configuration de base du site
  const siteConfig = {
    name: 'CRA Freelance',
    description: 'Générez gratuitement vos Comptes Rendus d\'Activité (CRA) en ligne. Signature électronique, validation client, export PDF. Pour freelances en régie.',
    url: 'https://cra-freelance.fr',
    logo: 'https://cra-freelance.fr/android-chrome-512x512.png',
    twitterHandle: '@crafreelance',
    locale: 'fr_FR'
  }

  /**
   * Génère les métadonnées SEO pour une page
   */
  const generateSeoMeta = (pageConfig: {
    title: string
    description?: string
    image?: string
    url?: string
    type?: 'website' | 'article'
    keywords?: string[]
    noIndex?: boolean
  }) => {
    const {
      title,
      description = siteConfig.description,
      image = siteConfig.logo,
      url = `${siteConfig.url}${route.path}`,
      type = 'website',
      keywords = [],
      noIndex = false
    } = pageConfig

    const fullTitle = title.includes(siteConfig.name) ? title : `${title} | ${siteConfig.name}`
    const fullDescription = description.length > 160 ? description.substring(0, 157) + '...' : description

    const fullImage = image.startsWith('http') ? image : `${siteConfig.url}${image}`

    const defaultKeywords = [
      'CRA',
      'CRA en ligne',
      'CRA gratuit',
      'CRA freelance',
      'compte rendu d\'activité',
      'compte rendu activité freelance',
      'freelance en régie',
      'mission régie',
      'génération CRA PDF',
      'signature CRA',
      'validation CRA client',
      'gestion administrative freelance',
      'freelance informatique',
      'modèle CRA',
      'template CRA freelance',
      'outil CRA en ligne gratuit'
    ]

    const fullKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ')

    return {
      title: fullTitle,
      description: fullDescription,
      keywords: fullKeywords,
      ogTitle: fullTitle,
      ogDescription: fullDescription,
      ogImage: fullImage,
      ogImageAlt: `${siteConfig.name} - ${title}`,
      ogUrl: url,
      ogType: type,
      ogSiteName: siteConfig.name,
      ogLocale: siteConfig.locale,
      twitterCard: 'summary_large_image' as const,
      twitterTitle: fullTitle,
      twitterDescription: fullDescription,
      twitterImage: fullImage,
      twitterImageAlt: `${siteConfig.name} - ${title}`,
      robots: noIndex ? 'noindex, nofollow' : 'index, follow',
      canonical: url
    }
  }

  /**
   * Génère les données structurées JSON-LD
   */
  const generateStructuredData = (pageConfig: {
    type: 'WebPage' | 'Article' | 'Product' | 'SoftwareApplication'
    title: string
    description?: string
    url?: string
    datePublished?: string
    dateModified?: string
    author?: string
    image?: string
  }) => {
    const {
      type,
      title,
      description = siteConfig.description,
      url = `${siteConfig.url}${route.path}`,
      datePublished,
      dateModified,
      author = siteConfig.name,
      image = siteConfig.logo
    } = pageConfig

    const baseData: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': type,
      name: title,
      description,
      url,
      image,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        logo: {
          '@type': 'ImageObject',
          url: siteConfig.logo
        }
      }
    }

    if (type === 'Article' && datePublished) {
      return {
        ...baseData,
        '@type': 'Article',
        headline: title,
        datePublished,
        dateModified: dateModified || datePublished,
        author: {
          '@type': 'Organization',
          name: author
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url
        }
      }
    }

    if (type === 'SoftwareApplication') {
      return {
        ...baseData,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: [
          {
            '@type': 'Offer',
            name: 'Offre Gratuite',
            price: '0',
            priceCurrency: 'EUR',
            description: 'Génération de CRA gratuite et illimitée avec signature et validation client'
          },
          {
            '@type': 'Offer',
            name: 'Offre Intégration',
            price: '4.99',
            priceCurrency: 'EUR',
            description: 'Tableau de bord, notifications, export CSV, intégration Pennylane / Indy / Tiime'
          }
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          ratingCount: '3',
          bestRating: '5',
          worstRating: '1'
        },
        featureList: [
          'Génération de CRA en ligne gratuite et illimitée',
          'Signature électronique',
          'Validation du CRA par le client final',
          'Export PDF',
          'Jours fériés automatiques',
          'Tableau de bord des CRA',
          'Notifications de validation',
          'Export CSV / Excel',
          'Intégration Pennylane, Indy, Tiime'
        ]
      }
    }

    return baseData
  }

  /**
   * Génère le schema BreadcrumbList
   */
  const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url
      }))
    }
  }

  /**
   * Génère le schema FAQPage
   */
  const generateFaqSchema = (faqs: { question: string; answer: string }[]) => {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }
  }

  /**
   * Applique les métadonnées SEO à une page
   */
  const setSeoMeta = (pageConfig: Parameters<typeof generateSeoMeta>[0] & {
    breadcrumbs?: { name: string; url: string }[]
    faqs?: { question: string; answer: string }[]
    datePublished?: string
    dateModified?: string
  }) => {
    const seoMeta = generateSeoMeta(pageConfig)

    useSeoMeta(seoMeta)

    const scripts: { type: string; innerHTML: string }[] = [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(generateStructuredData({
          type: pageConfig.type === 'article' ? 'Article' : 'WebPage',
          title: pageConfig.title,
          description: pageConfig.description,
          url: pageConfig.url,
          datePublished: pageConfig.datePublished,
          dateModified: pageConfig.dateModified
        }))
      }
    ]

    if (pageConfig.breadcrumbs) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(generateBreadcrumbSchema(pageConfig.breadcrumbs))
      })
    }

    if (pageConfig.faqs) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(generateFaqSchema(pageConfig.faqs))
      })
    }

    useHead({
      link: [
        { rel: 'canonical', href: seoMeta.canonical }
      ],
      script: scripts
    })
  }

  return {
    siteConfig,
    generateSeoMeta,
    generateStructuredData,
    generateBreadcrumbSchema,
    generateFaqSchema,
    setSeoMeta
  }
}
